import TFeedbackDefinition, {TFeedbackComponent, TFeedbackData} from "./TFeedbackDefinition";

type scalar = null|boolean|number|string;

export default class FeedbackModel {
    private static defaultValues = {
        radio: null,
        checkbox: false,
        textarea: '',
    };

    private readonly data: {[variableName: string]: scalar} = {};

    private variableMap: Map<string, TFeedbackComponent> = new Map();
    private visibilityMap: Map<TFeedbackComponent,boolean> = new Map();
    private dependencyMap: Map<TFeedbackComponent,Set<TFeedbackComponent>> = new Map();

    constructor(
        private readonly definition: TFeedbackDefinition
    ) {
        this.definition.form.components.forEach(componentDefinition => {
            if (componentDefinition.component === "radio" || componentDefinition.component === "checkbox" || componentDefinition.component === "textarea") {
                if (this.get(componentDefinition.variable) !== undefined) {
                    console.warn(`Feedback variable ${componentDefinition.variable} is already used. Component with second occurrence:`, componentDefinition);
                }
                this.variableMap.set(componentDefinition.variable, componentDefinition);
                this.set(componentDefinition.variable, null);
            }
        });
        this.definition.form.components.forEach(componentDefinition => {
           Object.keys(componentDefinition.dependsOn ?? {}).forEach(variableName => {
               const dependComponent = this.variableMap.get(variableName);
               if (!dependComponent) {
                   return;
               }
               const dependComponents = this.dependencyMap.get(dependComponent) ?? new Set();
               dependComponents.add(componentDefinition);

               this.dependencyMap.set(dependComponent, dependComponents);
           });
        });
    }

    private onHide(componentDefinition: TFeedbackComponent): void {
        if (componentDefinition.component === "radio" || componentDefinition.component === "checkbox" || componentDefinition.component === "textarea") {
            const value = this.data[componentDefinition.variable];
            if (value !== null) {
                this.set(componentDefinition.variable, null);
                this.dependencyMap.get(componentDefinition)?.forEach(dependComponent => this.updateVisibility(dependComponent));
            }
        }

    }

    private onShow(componentDefinition: TFeedbackComponent): void {
        if (componentDefinition.component === "radio" || componentDefinition.component === "checkbox" || componentDefinition.component === "textarea") {
            const value = this.data[componentDefinition.variable];
            const defaultValue = FeedbackModel.defaultValues[componentDefinition.component];
            if (value !== defaultValue) {
                this.data[componentDefinition.variable] = defaultValue;
                this.dependencyMap.get(componentDefinition)?.forEach(dependComponent => this.updateVisibility(dependComponent));
            }
        }
    }

    public set(variableName: string, value: scalar): void {
        this.data[variableName] = value;
        const componentDefinition = this.variableMap.get(variableName);
        if (componentDefinition) {
            this.dependencyMap.get(componentDefinition)?.forEach(dependComponent => this.updateVisibility(dependComponent));
        }
    }

    public get(variableName: string): scalar|undefined {
        return this.data[variableName]
    }

    public getData(): TFeedbackData {
        return {
            version: this.definition.form.version,
            data: this.data,
        };
    }

    public updateVisibility(component: TFeedbackComponent): boolean {
        const visible = this.isVisible(component);
        if (visible !== this.visibilityMap.get(component)) {
            if (visible) {
                this.onShow(component);
            } else {
                this.onHide(component);
            }
            this.visibilityMap.set(component, visible);
        }

        return visible;
    }

    public isVisible(component: TFeedbackComponent): boolean {
        // No dependency - visible always
        if (!component.dependsOn || Object.keys(component.dependsOn).length === 0) {
            return true;
        }

        return Object.entries(component.dependsOn).some(([variableName, needle]) => {
            const value = this.get(variableName);
            if (value === undefined) {
                console.warn(`Feedback variable '${variableName}' does not exist in component:`, component);
                return false;
            }
            if (Array.isArray(needle)) {
                if (needle.length === 0) {
                    return value;
                }
                return needle.some(needle => value === needle);
            }
            return needle === value;
        })
    }
}